import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import EMPDataCard from "../../../shared/components/EMPDataCard/EMPDataCard";
import { formatVehicleTrim } from "../../../utils/EmpUtil";

// Hardcoded for this story's demo until Nukleus OM provides spec-level detail.
const DEMO_VEHICLE_NAME = "2027 Fathom";

const toList = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }
  return value ? [value] : [];
};

const OtherColumn = ({ heading, items }) => (
  <Box>
    <Typography variant="caption">{heading}</Typography>

    {items.length === 0 ? (
      <Typography variant="body2">--</Typography>
    ) : items.length === 1 ? (
      <Typography variant="body2">{items[0]}</Typography>
    ) : (
      <Box>
        {items.map((item, index) => (
          <Typography
            component="p"
            variant="body2"
            key={`${heading}-${index}-${item}`}
          >
            {index + 1}. {item}
          </Typography>
        ))}
      </Box>
    )}
  </Box>
);

OtherColumn.propTypes = {
  heading: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const PreOrderVehicle = ({ content }) => {
  const { t } = useTranslation();
  const customerReservation = content?.customerReservation || {};

  const specData = [
    {
      heading: t("CustomerOrderDetails.preOrder.vehicle.specs.vin"),
      content: t("CustomerOrderDetails.preOrder.vehicle.vinValue"),
    },
    {
      heading: t("CustomerOrderDetails.preOrder.vehicle.specs.trim"),
      content: formatVehicleTrim(customerReservation.trim) || "--",
    },
    {
      heading: t("CustomerOrderDetails.preOrder.vehicle.specs.color"),
      content: customerReservation.color || "--",
    },
    {
      heading: t("CustomerOrderDetails.preOrder.vehicle.specs.drivetrain"),
      content: customerReservation.drivetrain || "--",
    },
    {
      heading: t("CustomerOrderDetails.preOrder.vehicle.specs.battery"),
      content: customerReservation.battery || "--",
    },
    {
      heading: t("CustomerOrderDetails.preOrder.vehicle.specs.wheels"),
      content: customerReservation.wheels || "--",
    },
  ];

  const otherColumns = [
    {
      heading: t("CustomerOrderDetails.preOrder.vehicle.other.accessories"),
      items: toList(customerReservation.accessories),
    },
    {
      heading: t("CustomerOrderDetails.preOrder.vehicle.other.elements"),
      items: toList(customerReservation.elements),
    },
    {
      heading: t("CustomerOrderDetails.preOrder.vehicle.other.subscriptions"),
      items: toList(customerReservation.subscriptions),
    },
    {
      heading: t(
        "CustomerOrderDetails.preOrder.vehicle.other.chargingEquipment"
      ),
      items: toList(customerReservation.chargingEquipment),
    },
  ];

  return (
    <Box
      display="grid"
      gridTemplateColumns="repeat(12, 1fr)"
      columnGap={4}
      rowGap={6}
      sx={{ mt: 5, mb: 11 }}
      data-testid="preorder-vehicle"
    >
      <Box gridColumn={{ xs: "span 12" }}>
        <Typography variant="h3" sx={{ color: "text.primary" }}>
          {DEMO_VEHICLE_NAME}
        </Typography>
      </Box>

      <Box gridColumn={{ xs: "span 12" }}>
        <EMPDataCard
          data={specData}
          hasBackground
          testId="preorder-vehicle-specs"
        />
      </Box>

      <Box gridColumn={{ xs: "span 12" }}>
        <Typography variant="h3" sx={{ color: "text.primary", mb: 2 }}>
          {t("CustomerOrderDetails.preOrder.vehicle.otherHeading")}
        </Typography>
        <Box
          sx={{
            border: "1px solid #B2B2B2",
            borderRadius: 2,
            padding: "16px",
          }}
          display="grid"
          gridTemplateColumns={{ xs: "1fr", md: "repeat(4, 1fr)" }}
          columnGap={4}
          rowGap={4}
          data-testid="preorder-vehicle-other"
        >
          {otherColumns.map((column) => (
            <OtherColumn
              key={column.heading}
              heading={column.heading}
              items={column.items}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

PreOrderVehicle.propTypes = {
  content: PropTypes.shape({
    customerReservation: PropTypes.shape({
      trim: PropTypes.string,
      color: PropTypes.string,
      drivetrain: PropTypes.string,
      battery: PropTypes.string,
      wheels: PropTypes.string,
      accessories: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ]),
      elements: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ]),
      subscriptions: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ]),
      chargingEquipment: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ]),
    }),
  }),
};

PreOrderVehicle.defaultProps = {
  content: null,
};

export default PreOrderVehicle;
